import React, { useEffect, useRef, useState } from "react";
import { Project, User, UserWithProjects } from "../types/api";

type UserWithProjectRole = User & { project_role?: string };

interface InvitedRow {
  id: number;
  email: string;
  role: string;
}

interface UserTableProps {
  users: User[] | UserWithProjectRole[] | UserWithProjects[];
  showActions?: boolean;
  onRemoveFromProject?: (userId: number, projectId: number) => Promise<void>;
  onAddToProject?: (userId: number, projectId: number) => Promise<void>;
  onDisable2FA?: (userId: number) => Promise<void>;
  onPromotePI?: (userId: number) => Promise<void>;
  onRevokePI?: (userId: number) => Promise<void>;
  viewerIsManager?: boolean; // Gates Promote/Revoke PI affordances
  viewerProjects?: Project[]; // Projects the viewer manages — drives Add/Remove entries in admin view
  projectViewMode?: boolean;
  currentProjectId?: number; // For direct "Remove from Project" action
  currentUserId?: number; // For checking if user can remove themselves
  projectOwnerId?: number | null; // projectViewMode only — shows an "Owner" badge next to that member's role
  showIdColumn?: boolean;
  showCreatedColumn?: boolean;
  actionLoading?: boolean;
  // Pending (not-yet-accepted) invitations for this project, rendered as extra
  // rows with an "Invited" status — projectViewMode only.
  invitedRows?: InvitedRow[];
  onRevokeInvite?: (invitationId: number) => void;
  onCopyInviteLink?: (invitationId: number) => void;
}

export const UserTable: React.FC<UserTableProps> = ({
  users,
  showActions = true,
  onRemoveFromProject,
  onAddToProject,
  onDisable2FA,
  onPromotePI,
  onRevokePI,
  viewerIsManager = false,
  viewerProjects,
  projectViewMode = false,
  currentProjectId,
  currentUserId,
  projectOwnerId,
  showIdColumn = true,
  showCreatedColumn = true,
  actionLoading = false,
  invitedRows = [],
  onRevokeInvite,
  onCopyInviteLink,
}) => {
  const [openMenuUserId, setOpenMenuUserId] = useState<number | null>(null);
  const [openSubmenu, setOpenSubmenu] = useState<"add" | "remove" | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    userId: number;
    action: string;
    projectId?: number;
    projectTitle?: string;
  } | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const closeMenu = () => {
    setOpenMenuUserId(null);
    setOpenSubmenu(null);
  };

  useEffect(() => {
    if (openMenuUserId === null) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        closeMenu();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openMenuUserId]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // In a project's member list, hide the viewer's own row — it's obvious they're
  // a member, and self-removal lives in project settings ("Leave Project").
  const displayedUsers =
    projectViewMode && currentUserId != null
      ? (users as Array<User | UserWithProjectRole | UserWithProjects>).filter(
          (u) => u.id !== currentUserId,
        )
      : users;

  const handleRemoveFromProject = async (userId: number, projectId: number) => {
    if (onRemoveFromProject) {
      await onRemoveFromProject(userId, projectId);
    }
    setConfirmDialog(null);
    closeMenu();
  };

  const handleAddToProject = async (userId: number, projectId: number) => {
    if (onAddToProject) {
      await onAddToProject(userId, projectId);
    }
    setConfirmDialog(null);
    closeMenu();
  };

  const handleDisable2FA = async (userId: number) => {
    if (onDisable2FA) {
      await onDisable2FA(userId);
    }
    setConfirmDialog(null);
    closeMenu();
  };

  const handlePromotePI = async (userId: number) => {
    if (onPromotePI) {
      await onPromotePI(userId);
    }
    setConfirmDialog(null);
    closeMenu();
  };

  const handleRevokePI = async (userId: number) => {
    if (onRevokePI) {
      await onRevokePI(userId);
    }
    setConfirmDialog(null);
    closeMenu();
  };

  return (
    <>
      <style>{`
        .actions-menu {
          position: relative;
        }
        .actions-button {
          background: none;
          border: none;
          font-size: 18px;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: var(--radius);
          color: var(--text-secondary);
        }
        .actions-button:hover {
          background: var(--surface-1);
        }
        .actions-dropdown {
          position: absolute;
          right: 0;
          top: 100%;
          background: var(--surface-2);
          border: 0.5px solid var(--border);
          border-radius: var(--radius);
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          z-index: 1000;
          min-width: 230px;
          margin-top: 4px;
        }
        /* Flex row with the disclosure caret pushed to the right edge, and
           nowrap: at 200px "Remove from project" wrapped and stranded its
           caret on a second line. */
        .actions-dropdown button {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          width: 100%;
          padding: 10px 16px;
          text-align: left;
          border: none;
          background: none;
          cursor: pointer;
          font-size: 14px;
          font-family: inherit;
          color: var(--text-primary);
          border-bottom: 0.5px solid var(--border);
          white-space: nowrap;
        }
        .actions-dropdown button:hover {
          background: var(--surface-1);
        }
        .actions-dropdown button:last-child {
          border-bottom: none;
        }
        .actions-dropdown button.danger {
          color: var(--text-danger);
        }
        .actions-dropdown button.danger:hover {
          background: var(--bg-danger);
        }
        .submenu {
          padding-left: 16px;
          background: var(--surface-1);
        }
        .submenu button {
          padding: 8px 16px;
          font-size: 13px;
        }
        .inline-row-action {
          padding: 4px 10px;
          font-size: 12px;
          font-family: inherit;
          border-radius: var(--radius);
          border: 0.5px solid var(--border-strong);
          background: var(--surface-2);
          cursor: pointer;
          margin-right: 6px;
        }
        .inline-row-action:hover {
          background: var(--surface-1);
        }
        .inline-row-action.danger {
          color: var(--text-danger);
        }
        .inline-row-action.danger:hover {
          background: var(--bg-danger);
        }
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
        }
        .modal-content {
          background: white;
          padding: 24px;
          border-radius: 8px;
          max-width: 500px;
          width: 90%;
          box-shadow: 0 4px 16px rgba(0,0,0,0.2);
        }
        .modal-buttons {
          display: flex;
          gap: 12px;
          margin-top: 20px;
          justify-content: flex-end;
        }
      `}</style>

      <div className="data-table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              {showIdColumn && <th>ID</th>}
              <th>Name</th>
              <th>Email</th>
              <th>Institution</th>
              <th>Status</th>
              <th>Role</th>
              {!projectViewMode && <th>Projects</th>}
              {showCreatedColumn && <th>Created</th>}
              {showActions && <th style={{ width: "50px" }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {displayedUsers.map((user) => {
              const userWithRole = user as UserWithProjectRole;
              return (
                <tr
                  key={user.id}
                  style={{
                    opacity: !user.two_fa_enabled ? 0.6 : 1,
                    backgroundColor: !user.two_fa_enabled
                      ? "var(--bg-warning)"
                      : "transparent",
                  }}
                  title={
                    !user.two_fa_enabled
                      ? "⚠️ 2FA in reset mode - user must set up 2FA on next login"
                      : ""
                  }
                >
                  {showIdColumn && <td>{user.id}</td>}
                  <td>{user.name}</td>
                  <td>{user.email}</td>
                  <td>{user.institution}</td>
                  <td>
                    <span
                      className={`badge ${
                        !user.two_fa_enabled
                          ? "warning"
                          : user.is_active
                            ? "active"
                            : "inactive"
                      }`}
                    >
                      {!user.two_fa_enabled
                        ? "Pending Activation"
                        : user.is_active
                          ? "Active"
                          : "Inactive"}
                    </span>
                  </td>
                  <td>
                    {projectViewMode ? (
                      // In project view, show project-specific role
                      <>
                        {userWithRole.project_role === "pi" && (
                          <span className="badge pi">PI</span>
                        )}
                        {userWithRole.project_role === "researcher" &&
                          "Researcher"}
                        {!userWithRole.project_role && "-"}
                        {projectOwnerId != null &&
                          user.id === projectOwnerId && (
                            <span
                              className="badge owner"
                              style={{ marginLeft: 4 }}
                            >
                              Owner
                            </span>
                          )}
                      </>
                    ) : (
                      // In admin view, show organizational role
                      <>
                        {user.role === "manager" && (
                          <span className="badge admin">Manager</span>
                        )}
                        {user.role === "data_uploader" && (
                          <span className="badge uploader">Data Uploader</span>
                        )}
                        {user.role === "pi" && (
                          <span className="badge pi">PI</span>
                        )}
                        {user.role === "researcher" && "Researcher"}
                      </>
                    )}
                  </td>
                  {!projectViewMode && (
                    <td>
                      {(user as UserWithProjects).projects?.length ? (
                        (user as UserWithProjects).projects!.map((p) => (
                          <span
                            key={p.project_id}
                            style={{
                              display: "inline-block",
                              padding: "2px 8px",
                              background: "#e8e0f0",
                              color: "#5b21b6",
                              borderRadius: "12px",
                              fontSize: "12px",
                              marginRight: "4px",
                              marginBottom: "2px",
                            }}
                          >
                            {p.project_name}
                          </span>
                        ))
                      ) : (
                        <span style={{ color: "#94a3b8" }}>—</span>
                      )}
                    </td>
                  )}
                  {showCreatedColumn && <td>{formatDate(user.created_at)}</td>}
                  {showActions && (
                    <td>
                      {projectViewMode ? (
                        // Project view: 3-dot menu with direct actions (no submenus)
                        <div
                          className="actions-menu"
                          ref={openMenuUserId === user.id ? menuRef : undefined}
                        >
                          <button
                            className="actions-button"
                            onClick={() => {
                              if (openMenuUserId === user.id) {
                                closeMenu();
                              } else {
                                setOpenMenuUserId(user.id);
                                setOpenSubmenu(null);
                              }
                            }}
                            title="User actions"
                          >
                            ⋮
                          </button>
                          {openMenuUserId === user.id && (
                            <div className="actions-dropdown">
                              <button
                                onClick={() => {
                                  if (currentProjectId && onRemoveFromProject) {
                                    setConfirmDialog({
                                      userId: user.id,
                                      action: "remove",
                                      projectId: currentProjectId,
                                    });
                                  }
                                }}
                                disabled={
                                  userWithRole.project_role === "pi" &&
                                  user.id !== currentUserId
                                }
                                title={
                                  userWithRole.project_role === "pi" &&
                                  user.id !== currentUserId
                                    ? "PIs cannot remove other PIs. Contact an org admin."
                                    : undefined
                                }
                              >
                                ➖ Remove from Project
                              </button>
                              {user.two_fa_enabled && (
                                <button
                                  className="danger"
                                  onClick={() =>
                                    setConfirmDialog({
                                      userId: user.id,
                                      action: "disable2fa",
                                    })
                                  }
                                >
                                  🔓 Disable 2FA
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        // Admin view: 3-dot menu with manager-only PI promote/revoke
                        <div
                          className="actions-menu"
                          ref={openMenuUserId === user.id ? menuRef : undefined}
                        >
                          <button
                            className="actions-button"
                            onClick={() => {
                              if (openMenuUserId === user.id) {
                                closeMenu();
                              } else {
                                setOpenMenuUserId(user.id);
                                setOpenSubmenu(null);
                              }
                            }}
                            title="User actions"
                          >
                            ⋮
                          </button>
                          {openMenuUserId === user.id &&
                            (() => {
                              const userProjects =
                                (user as UserWithProjects).projects || [];
                              const userProjectIds = new Set(
                                userProjects.map((p) => p.project_id),
                              );
                              const addable = (viewerProjects || []).filter(
                                (vp) => !userProjectIds.has(vp.id),
                              );
                              const removable = (viewerProjects || []).filter(
                                (vp) => userProjectIds.has(vp.id),
                              );
                              return (
                                <div className="actions-dropdown">
                                  {onAddToProject && addable.length > 0 && (
                                    <>
                                      <button
                                        onClick={() =>
                                          setOpenSubmenu(
                                            openSubmenu === "add"
                                              ? null
                                              : "add",
                                          )
                                        }
                                      >
                                        <span>➕ Add to project</span>
                                        <span>
                                          {openSubmenu === "add" ? "▾" : "▸"}
                                        </span>
                                      </button>
                                      {openSubmenu === "add" && (
                                        <div className="submenu">
                                          {addable.map((vp) => (
                                            <button
                                              key={`add-${vp.id}`}
                                              onClick={() =>
                                                setConfirmDialog({
                                                  userId: user.id,
                                                  action: "add",
                                                  projectId: vp.id,
                                                  projectTitle: vp.title,
                                                })
                                              }
                                            >
                                              {vp.title}
                                            </button>
                                          ))}
                                        </div>
                                      )}
                                    </>
                                  )}
                                  {onRemoveFromProject &&
                                    removable.length > 0 && (
                                      <>
                                        <button
                                          className="danger"
                                          onClick={() =>
                                            setOpenSubmenu(
                                              openSubmenu === "remove"
                                                ? null
                                                : "remove",
                                            )
                                          }
                                        >
                                          <span>➖ Remove from project</span>
                                          <span>
                                            {openSubmenu === "remove"
                                              ? "▾"
                                              : "▸"}
                                          </span>
                                        </button>
                                        {openSubmenu === "remove" && (
                                          <div className="submenu">
                                            {removable.map((vp) => (
                                              <button
                                                key={`remove-${vp.id}`}
                                                className="danger"
                                                onClick={() =>
                                                  setConfirmDialog({
                                                    userId: user.id,
                                                    action: "remove",
                                                    projectId: vp.id,
                                                    projectTitle: vp.title,
                                                  })
                                                }
                                              >
                                                {vp.title}
                                              </button>
                                            ))}
                                          </div>
                                        )}
                                      </>
                                    )}
                                  {viewerIsManager &&
                                    onPromotePI &&
                                    user.role !== "pi" &&
                                    user.role !== "manager" &&
                                    user.role !== "data_uploader" && (
                                      <button
                                        onClick={() =>
                                          setConfirmDialog({
                                            userId: user.id,
                                            action: "promotePI",
                                          })
                                        }
                                      >
                                        ⬆ Promote to PI
                                      </button>
                                    )}
                                  {viewerIsManager &&
                                    onRevokePI &&
                                    user.role === "pi" && (
                                      <button
                                        className="danger"
                                        onClick={() =>
                                          setConfirmDialog({
                                            userId: user.id,
                                            action: "revokePI",
                                          })
                                        }
                                      >
                                        ⬇ Revoke PI
                                      </button>
                                    )}
                                  {user.two_fa_enabled && onDisable2FA && (
                                    <button
                                      className="danger"
                                      onClick={() =>
                                        setConfirmDialog({
                                          userId: user.id,
                                          action: "disable2fa",
                                        })
                                      }
                                    >
                                      🔓 Disable 2FA
                                    </button>
                                  )}
                                </div>
                              );
                            })()}
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
            {invitedRows.map((invite) => (
              <tr key={`invite-${invite.id}`}>
                {showIdColumn && (
                  <td style={{ color: "var(--text-muted)" }}>—</td>
                )}
                <td style={{ color: "var(--text-muted)" }}>—</td>
                <td>{invite.email}</td>
                <td style={{ color: "var(--text-muted)" }}>—</td>
                <td>
                  <span className="badge invited">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 6v6l4 2" />
                    </svg>
                    Invited
                  </span>
                </td>
                <td>
                  {invite.role === "pi" ? (
                    <span className="badge pi">PI</span>
                  ) : (
                    "Researcher"
                  )}
                </td>
                {!projectViewMode && (
                  <td style={{ color: "var(--text-muted)" }}>—</td>
                )}
                {showCreatedColumn && (
                  <td style={{ color: "var(--text-muted)" }}>—</td>
                )}
                {showActions && (
                  <td>
                    {onCopyInviteLink && (
                      <button
                        className="inline-row-action"
                        onClick={() => onCopyInviteLink(invite.id)}
                      >
                        Copy link
                      </button>
                    )}
                    {onRevokeInvite && (
                      <button
                        className="inline-row-action danger"
                        onClick={() => onRevokeInvite(invite.id)}
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Confirmation Dialog */}
      {confirmDialog && (
        <div className="modal-overlay" onClick={() => setConfirmDialog(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Action</h3>
            {confirmDialog.action === "disable2fa" && (
              <p>Are you sure you want to disable 2FA for this user? They will be required to set up 2FA again on their next login.</p>
            )}
            {confirmDialog.action === "remove" && confirmDialog.projectId && (
              <p>
                Are you sure you want to remove this user from
                {confirmDialog.projectTitle ? ` "${confirmDialog.projectTitle}"` : " the project"}?
              </p>
            )}
            {confirmDialog.action === "add" && confirmDialog.projectId && (
              <p>
                Add this user as a researcher to
                {confirmDialog.projectTitle ? ` "${confirmDialog.projectTitle}"` : " the project"}?
              </p>
            )}
            {confirmDialog.action === "promotePI" && (
              <p>Promote this user to Principal Investigator? They will be able to create projects and manage their own research projects.</p>
            )}
            {confirmDialog.action === "revokePI" && (
              <p>Revoke this user's PI status? They will be demoted to Researcher and removed as PI from any active projects they currently lead.</p>
            )}
            <div className="modal-buttons">
              <button
                onClick={() => setConfirmDialog(null)}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (confirmDialog.action === "disable2fa") {
                    handleDisable2FA(confirmDialog.userId);
                  } else if (confirmDialog.action === "remove" && confirmDialog.projectId) {
                    handleRemoveFromProject(confirmDialog.userId, confirmDialog.projectId);
                  } else if (confirmDialog.action === "add" && confirmDialog.projectId) {
                    handleAddToProject(confirmDialog.userId, confirmDialog.projectId);
                  } else if (confirmDialog.action === "promotePI") {
                    handlePromotePI(confirmDialog.userId);
                  } else if (confirmDialog.action === "revokePI") {
                    handleRevokePI(confirmDialog.userId);
                  }
                }}
                disabled={actionLoading}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#dc3545",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: actionLoading ? "not-allowed" : "pointer",
                }}
              >
                {actionLoading ? "Processing..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
